import React, { useState, useEffect } from 'react';
import { useAuth } from '../pages/AuthContext';
import { useNavigate } from 'react-router-dom';
import { API_ENDPOINTS, apiRequest } from '../utils/api';

export default function Profile() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [userVideos, setUserVideos] = useState([]);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState(null);
  
  // 添加视频表单状态
  const [showAddForm, setShowAddForm] = useState(false);
  const [newVideo, setNewVideo] = useState({
    title: '',
    author: user ? user.username : '',
    views: '0',
    video_text: '',
    date: new Date().toISOString().split('T')[0],
    duration: '00:00',
    thumbnail: '',
    url: '',
    authorAvatar: '',
    praise: '0'
  });
  
  // 编辑视频状态
  const [editingVideo, setEditingVideo] = useState(null);
  const [editVideoForm, setEditVideoForm] = useState({
    title: '',
    video_text: '',
    thumbnail: '',
    url: '',
    duration: '',
    praise: '0'
  });

  // 检查用户是否已登录
  useEffect(() => {
    if (!user) {
      navigate('/login');
    }
  }, [user, navigate]);

  // 获取用户发布的视频
  useEffect(() => {
    if (user) {
      fetchUserVideos();
    }
  }, [user]);

  const fetchUserVideos = async () => {
    setIsLoading(true);
    setError(null);
    
    try {
      // 使用专门的用户视频API获取用户发布的视频
      const response = await apiRequest(`${API_ENDPOINTS.USER_VIDEOS}?author=${user.username}`);
      if (!response.ok) throw new Error('获取视频失败');
      
      const videos = await response.json();
      setUserVideos(videos);
    } catch (err) {
      setError(err.message);
    } finally {
      setIsLoading(false);
    }
  };

  const handleAddVideo = async (e) => {
    e.preventDefault();
    
    try {
      const response = await apiRequest(API_ENDPOINTS.USER_VIDEOS, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          ...newVideo,
          author: user.username, // 确保作者是当前用户
          praise: "0",
          authorAvatar: user.avatar || ''
        }),
      });

      if (!response.ok) throw new Error('添加视频失败');

      // 重新获取用户视频列表
      await fetchUserVideos();
      
      // 重置表单并隐藏
      setNewVideo({
        title: '',
        author: user.username,
        views: '0',
        video_text: '',
        date: new Date().toISOString().split('T')[0],
        duration: '00:00',
        thumbnail: '',
        url: '',
        authorAvatar: '',
        praise: '0'
      });
      setShowAddForm(false);
    } catch (err) {
      setError(err.message);
    }
  };

  const handleUpdateVideo = async (e) => {
    e.preventDefault();
    
    try {
      const response = await apiRequest(`${API_ENDPOINTS.USER_VIDEOS}?id=${editingVideo.id}`, {
        method: 'PATCH',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          ...editVideoForm,
          praise: editingVideo.praise || '0'  // 保留原有的点赞数
        }),
      });

      if (!response.ok) throw new Error('更新视频失败');

      // 重新获取用户视频列表
      await fetchUserVideos();
      
      // 重置编辑状态
      setEditingVideo(null);
      setEditVideoForm({
        title: '',
        video_text: '',
        thumbnail: '',
        url: '',
        duration: '',
        praise: '0'
      });
    } catch (err) {
      setError(err.message);
    }
  };

  const handleDeleteVideo = async (videoId) => {
    if (!window.confirm('确定要删除这个视频吗？')) return;
    
    try {
      const response = await apiRequest(`${API_ENDPOINTS.USER_VIDEOS}?id=${videoId}`, {
        method: 'DELETE'
      });
      
      if (!response.ok) throw new Error('删除视频失败');
      
      // 重新获取用户视频列表
      await fetchUserVideos();
    } catch (err) {
      setError(err.message);
    }
  };

  const handleEditVideo = (video) => {
    setEditingVideo(video);
    setEditVideoForm({
      title: video.title,
      video_text: video.video_text,
      thumbnail: video.thumbnail,
      url: video.url,
      duration: video.duration,
      praise: video.praise || '0'
    });
  };

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setNewVideo(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const handleEditVideoChange = (e) => {
    const { name, value } = e.target;
    setEditVideoForm(prev => ({
      ...prev,
      [name]: value
    }));
  };

  if (!user) {
    return (
      <div className="container mx-auto px-4 py-8">
        <p>请先登录以查看个人空间</p>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50 py-8">
      <div className="container mx-auto px-4">
        <div className="bg-white rounded-xl shadow-md p-6 mb-8">
          <div className="flex items-center">
            {user.avatar ? (
              <img 
                src={user.avatar} 
                alt="用户头像"
                className="w-16 h-16 rounded-full object-cover"
                onError={(e) => {
                  e.target.style.display = 'none';
                  e.target.nextSibling.style.display = 'flex';
                }}
              />
            ) : null}
            <div className="w-16 h-16 rounded-full flex items-center justify-center bg-gray-200" 
                 style={{ display: user.avatar ? 'none' : 'flex' }}>
              <svg className="w-8 h-8 text-blue-500" fill="currentColor" viewBox="0 0 20 20">
                <path fillRule="evenodd" d="M10 9a3 3 0 100-6 3 3 0 000 6zm-7 9a7 7 0 1114 0H3z" clipRule="evenodd"></path>
              </svg>
            </div>
            <div className="ml-4">
              <h1 className="text-2xl font-bold text-gray-900">{user.username} 的个人空间</h1>
              <p className="text-gray-600">欢迎来到你的个人空间</p>
            </div>
          </div>
        </div>

        <div className="bg-white rounded-xl shadow-md p-6">
          <div className="flex flex-col md:flex-row md:items-center md:justify-between mb-6">
            <h2 className="text-2xl font-bold text-gray-900 mb-4 md:mb-0">我的视频</h2>
            <button
              onClick={() => setShowAddForm(!showAddForm)}
              className="bg-blue-500 hover:bg-blue-600 text-white px-4 py-2 rounded-lg transition duration-300 flex items-center"
            >
              <svg className="w-5 h-5 mr-1" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 6v6m0 0v6m0-6h6m-6 0H6" />
              </svg>
              {showAddForm ? '取消添加' : '添加视频'}
            </button>
          </div>

          {/* 添加视频表单 */}
          {showAddForm && (
            <div className="bg-gray-50 rounded-xl p-6 mb-8 animate-fadeIn">
              <h3 className="text-xl font-bold text-gray-900 mb-4">添加新视频</h3>
              <form onSubmit={handleAddVideo}>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">视频标题 *</label>
                    <input
                      type="text"
                      name="title"
                      value={newVideo.title}
                      onChange={handleInputChange}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                      required
                    />
                  </div>
                  
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">视频链接 *</label>
                    <input
                      type="text"
                      name="url"
                      value={newVideo.url}
                      onChange={handleInputChange}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                      required
                    />
                  </div>
                  
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">封面链接</label>
                    <input
                      type="text"
                      name="thumbnail"
                      value={newVideo.thumbnail}
                      onChange={handleInputChange}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                    />
                  </div>
                  
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">视频时长</label>
                    <input
                      type="text"
                      name="duration"
                      value={newVideo.duration}
                      onChange={handleInputChange}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                      placeholder="00:00"
                    />
                  </div>
                  
                  <div className="md:col-span-2">
                    <label className="block text-sm font-medium text-gray-700 mb-1">视频描述</label>
                    <textarea
                      name="video_text"
                      value={newVideo.video_text}
                      onChange={handleInputChange}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                      rows="3"
                    />
                  </div>
                </div>
                
                <div className="flex justify-end space-x-4 mt-6">
                  <button
                    type="button"
                    onClick={() => setShowAddForm(false)}
                    className="px-4 py-2 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50 transition duration-300"
                  >
                    取消
                  </button>
                  <button
                    type="submit"
                    className="px-4 py-2 bg-blue-500 text-white rounded-lg hover:bg-blue-600 transition duration-300"
                  >
                    添加视频
                  </button>
                </div>
              </form>
            </div>
          )}

          {/* 编辑视频表单 */}
          {editingVideo && (
            <div className="bg-gray-50 rounded-xl p-6 mb-8 animate-fadeIn">
              <h3 className="text-xl font-bold text-gray-900 mb-4">编辑视频</h3>
              <form onSubmit={handleUpdateVideo}>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">视频标题 *</label>
                    <input
                      type="text"
                      name="title"
                      value={editVideoForm.title}
                      onChange={handleEditVideoChange}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                      required
                    />
                  </div>
                  
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">视频链接 *</label>
                    <input
                      type="text"
                      name="url"
                      value={editVideoForm.url}
                      onChange={handleEditVideoChange}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                      required
                    />
                  </div>
                  
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">封面链接</label>
                    <input
                      type="text"
                      name="thumbnail"
                      value={editVideoForm.thumbnail}
                      onChange={handleEditVideoChange}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                    />
                  </div>
                  
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">视频时长</label>
                    <input
                      type="text"
                      name="duration"
                      value={editVideoForm.duration}
                      onChange={handleEditVideoChange}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                      placeholder="00:00"
                    />
                  </div>
                  
                  <div className="md:col-span-2">
                    <label className="block text-sm font-medium text-gray-700 mb-1">视频描述</label>
                    <textarea
                      name="video_text"
                      value={editVideoForm.video_text}
                      onChange={handleEditVideoChange}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                      rows="3"
                    />
                  </div>
                </div>
                
                <div className="flex justify-end space-x-4 mt-6">
                  <button
                    type="button"
                    onClick={() => setEditingVideo(null)}
                    className="px-4 py-2 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50 transition duration-300"
                  >
                    取消
                  </button>
                  <button
                    type="submit"
                    className="px-4 py-2 bg-blue-500 text-white rounded-lg hover:bg-blue-600 transition duration-300"
                  >
                    保存更改
                  </button>
                </div>
              </form>
            </div>
          )}

          {/* 视频列表 */}
          {isLoading ? (
            <div className="flex justify-center items-center h-64">
              <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-blue-500"></div>
            </div>
          ) : error ? (
            <div className="bg-red-50 border-l-4 border-red-500 p-4 rounded">
              <div className="flex">
                <div className="flex-shrink-0">
                  <svg className="h-5 w-5 text-red-400" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 20 20" fill="currentColor">
                    <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zM8.707 7.293a1 1 0 00-1.414 1.414L8.586 10l-1.293 1.293a1 1 0 101.414 1.414L10 11.414l1.293 1.293a1 1 0 001.414-1.414L11.414 10l1.293-1.293a1 1 0 00-1.414-1.414L10 8.586 8.707 7.293z" clipRule="evenodd" />
                  </svg>
                </div>
                <div className="ml-3">
                  <p className="text-sm text-red-700">
                    {error}
                  </p>
                </div>
              </div>
            </div>
          ) : userVideos.length === 0 ? (
            <div className="text-center py-12">
              <svg className="mx-auto h-12 w-12 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 10l4.553-2.276A1 1 0 0121 8.618v6.764a1 1 0 01-1.447.894L15 14M5 18h8a2 2 0 002-2V8a2 2 0 00-2-2H5a2 2 0 00-2 2v8a2 2 0 002 2z" />
              </svg>
              <h3 className="mt-2 text-sm font-medium text-gray-900">暂无视频</h3>
              <p className="mt-1 text-sm text-gray-500">开始添加你的第一个视频吧</p>
            </div>
          ) : (
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6">
              {userVideos.map((video) => (
                <div key={video.id} className="bg-gray-50 rounded-lg overflow-hidden shadow hover:shadow-md transition duration-300">
                  <div className="relative pb-[56.25%]"> {/* 16:9 宽高比 */}
                    <img 
                      src={video.thumbnail || '/img/default_thumbnail.jpg'} 
                      alt={video.title}
                      className="absolute inset-0 w-full h-full object-cover"
                      onError={(e) => {
                        e.target.src = '/img/default_thumbnail.jpg';
                      }}
                    />
                    <div className="absolute bottom-2 right-2 bg-black bg-opacity-70 text-white text-xs px-1 py-0.5 rounded">
                      {video.duration}
                    </div>
                  </div>
                  <div className="p-4">
                    <h3 className="font-medium text-gray-900 line-clamp-2 mb-2">{video.title}</h3>
                    <div className="flex items-center text-sm text-gray-500 mb-3">
                      <span>{video.views} 次播放</span>
                      <span className="mx-2">•</span>
                      <span>{video.date}</span>
                    </div>
                    <div className="flex justify-between items-center">
                      <div className="text-sm text-gray-500">
                        <span className="flex items-center">
                          <svg className="w-4 h-4 mr-1" fill="currentColor" viewBox="0 0 20 20">
                            <path d="M2 10.5a1.5 1.5 0 113 0v6a1.5 1.5 0 01-3 0v-6zM6 10.333v5.43a2 2 0 001.106 1.79l.05.025A4 4 0 008.943 18h5.416a2 2 0 001.962-1.608l1.2-6A2 2 0 0015.583 8H12V4a2 2 0 00-2-2 1 1 0 00-1 1v.667a4 4 0 00-.8 2.4L6.8 7.933a4 4 0 00-.8 2.4z" />
                          </svg>
                          {video.praise || 0}
                        </span>
                      </div>
                      <div className="flex justify-end space-x-2">
                        <button
                          onClick={() => handleEditVideo(video)}
                          className="text-blue-500 hover:text-blue-700"
                        >
                          编辑
                        </button>
                        <button
                          onClick={() => handleDeleteVideo(video.id)}
                          className="text-red-500 hover:text-red-700"
                        >
                          删除
                        </button>
                      </div>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}